import { membershipQueries } from "@app/config/queryFactory";
import { createPaymentMethod } from "@features/membership/api/paymentMethods";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const serviceUrl = typeof window !== "undefined" ? window.location.origin : "";

const useAddPaymentMethod = () => {
  const { t } = useTranslation();
  const paymentWindowRef = useRef<Window | null>(null);

  const addPaymentMethodMutation = useMutation({
    mutationFn: createPaymentMethod,
    onSuccess: (data) => {
      const redirectUrl = encodeURIComponent(data.hosted_page);

      const newWindow = window.open(`${serviceUrl}/payment/callback?redirectUrl=${redirectUrl}`);

      if (!newWindow || newWindow.closed || typeof newWindow.closed === "undefined") {
        toast.error(t("apply.payment.failedToOpenPaymentWindow"));
        return;
      }

      paymentWindowRef.current = newWindow;
    },
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      const ALLOWED_ORIGIN = window.location.origin;

      // (A) Origin 검증
      if (event.origin !== ALLOWED_ORIGIN) return;

      // (B) Source 검증: 우리가 연 팝업이 보낸 것인지 확인
      //     팝업이 차단/재생성되면 달라질 수 있으니 상황에 따라 완화 가능
      if (!paymentWindowRef.current || event.source !== paymentWindowRef.current) return;

      const data = event.data as { type: "PAYMENT_SUCCEEDED" | "PAYMENT_FAILED" };
      if (!data || typeof data !== "object" || !("type" in data)) return;

      // (C) 메시지 타입별 처리
      if (data.type === "PAYMENT_SUCCEEDED") {
        queryClient.invalidateQueries({ queryKey: membershipQueries.paymentMethodsKey(), refetchType: "all" });
        toast.success(t("paymentMethodAddedSuccess"));

        queryClient.invalidateQueries({ queryKey: membershipQueries.membershipKey(), refetchType: "all" });
        queryClient.invalidateQueries({ queryKey: membershipQueries.autoPayKey(), refetchType: "all" });
      }

      paymentWindowRef.current = null;
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [queryClient, t]);

  return addPaymentMethodMutation;
};

export default useAddPaymentMethod;
